import { useRef } from 'react'
import { Link } from 'react-router'
import { useReveal } from './__use-reveal.ts'

interface FinalCtaLink {
	/** Visible label for the button. */
	label: string
	/** Target route. */
	to: string
}

interface FinalCtaProps {
	heading: string
	lead: string
	primaryCta: FinalCtaLink
	secondaryCta: FinalCtaLink
	/**
	 * Section landmark id (and `${id}-heading` for the label). Defaults to
	 * `contact` so the home page keeps the anchor the hero/header CTAs point to;
	 * other pages (e.g. `/features`, `/faq`) pass their own to avoid duplicate ids.
	 */
	id?: string
}

/**
 * Closing call-to-action band. Presentational shell — all copy and CTA targets
 * are passed in, so `/features` and `/faq` reuse it with per-page copy pointing
 * at `/contact` while the home page renders its own. The band, ambient glow, and
 * the light button's text colour are all derived from the brand accent via
 * `oklch(from var(--brand) …)`, so the band re-themes with the accent and stays
 * readable in light and dark (literal white text on the dark brand gradient is
 * the sanctioned exception). The band reveals on scroll via `useReveal`
 * (progressive enhancement — the resting state renders server-side).
 */
export function FinalCta({
	heading,
	lead,
	primaryCta,
	secondaryCta,
	id = 'contact',
}: FinalCtaProps) {
	const ref = useRef<HTMLElement>(null)
	useReveal(ref)

	return (
		<section
			id={id}
			ref={ref}
			aria-labelledby={`${id}-heading`}
			className="container scroll-mt-20 py-24"
		>
			<div
				data-reveal
				className="relative overflow-hidden rounded-3xl px-6 py-16 text-center text-white bg-[linear-gradient(135deg,oklch(from_var(--brand)_calc(l-0.13)_c_h),oklch(from_var(--brand)_calc(l-0.26)_calc(c*0.7)_h))] sm:px-12 sm:py-20"
			>
				<div
					aria-hidden
					className="pointer-events-none absolute -top-32 -right-24 h-[420px] w-[420px] blur-lg bg-[radial-gradient(closest-side,oklch(from_var(--brand)_calc(l+0.13)_c_h/0.5),transparent_70%)]"
				/>
				<h2
					id={`${id}-heading`}
					className="relative text-3xl font-semibold tracking-h2 text-balance sm:text-4xl"
				>
					{heading}
				</h2>
				<p className="relative mx-auto mt-4 max-w-[48ch] text-lg text-pretty text-white/80">
					{lead}
				</p>
				<div className="relative mt-8 flex flex-wrap justify-center gap-3">
					<Link
						to={primaryCta.to}
						data-slot="button"
						className="inline-flex h-11 items-center rounded-md bg-white px-6 font-semibold text-[oklch(from_var(--brand)_calc(l-0.25)_c_h)] transition-transform hover:-translate-y-px"
					>
						{primaryCta.label}
					</Link>
					<Link
						to={secondaryCta.to}
						data-slot="button"
						className="inline-flex h-11 items-center rounded-md border border-white/40 px-6 font-medium text-white transition-colors duration-[var(--duration-fast)] ease-out hover:bg-white/10"
					>
						{secondaryCta.label}
					</Link>
				</div>
			</div>
		</section>
	)
}
